import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, PrimaryButton, GhostButton, TextField, Label } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function FinishedGoods() {
  const [rows, setRows] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ sku: "", name: "", description: "", uom: "", unit_price: "", status: "active" });

  const load = async () => {
    try { const { data } = await api.get("/finished-goods"); setRows(data.data); setCanEdit(data.can_edit); }
    catch { toast.error("Failed to load finished goods"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ sku: "", name: "", description: "", uom: "", unit_price: "", status: "active" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ sku: r.sku || "", name: r.name || "", description: r.description || "", uom: r.uom || "", unit_price: r.unit_price ?? "", status: r.status || "active" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, unit_price: form.unit_price === "" ? null : Number(form.unit_price) };
      if (editing) await api.put(`/finished-goods/${editing.id}`, body);
      else await api.post("/finished-goods", body);
      toast.success(editing ? "Finished good updated" : "Finished good added");
      setOpen(false); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
  };

  return (
    <div className="space-y-6" data-testid="finished-goods-page">
      <PageHeader title="Finished Goods" subtitle="All finished products and their current stock — visible to everyone."
        actions={canEdit && <PrimaryButton onClick={openNew} data-testid="fg-add">Add Finished Good</PrimaryButton>} />
      <Panel className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="fg-table">
          <thead><tr className="text-left font-mono text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
            <th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Unit</th>
            <th className="px-4 py-3 text-right">Unit Price</th><th className="px-4 py-3 text-right">On Hand</th>
            {canEdit && <th className="px-4 py-3 text-right">Actions</th>}
          </tr></thead>
          <tbody>{rows.length === 0 ? <tr><td colSpan={canEdit ? 6 : 5} className="px-4 py-8 text-center text-slate-500">No finished goods yet.</td></tr>
            : rows.map((r) => (<tr key={r.id} className="border-b border-slate-800/60" data-testid={`fg-row-${r.sku}`}>
              <td className="px-4 py-3 font-mono text-orange-400">{r.sku}</td>
              <td className="px-4 py-3">{r.name}</td>
              <td className="px-4 py-3 text-slate-400">{r.uom || "—"}</td>
              <td className="px-4 py-3 text-right font-mono text-slate-300">{r.unit_price != null ? Number(r.unit_price).toLocaleString() : "—"}</td>
              <td className="px-4 py-3 text-right font-mono text-slate-100">{Number(r.on_hand || 0).toLocaleString()}</td>
              {canEdit && <td className="px-4 py-3 text-right"><GhostButton onClick={() => openEdit(r)} data-testid={`fg-edit-${r.sku}`}>Edit</GhostButton></td>}
            </tr>))}</tbody>
        </table>
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-wide">{editing ? "Edit" : "Add"} Finished Good</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Code</Label><TextField required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="fg-field-sku" disabled={!!editing} /></div>
            <div><Label>Name</Label><TextField required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fg-field-name" /></div>
            <div><Label>Unit</Label><TextField value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} data-testid="fg-field-uom" /></div>
            <div><Label>Unit Price</Label><TextField type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="fg-field-price" /></div>
            <div><Label>Description</Label><TextField value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="fg-field-desc" /></div>
            <DialogFooter><GhostButton type="button" onClick={() => setOpen(false)}>Cancel</GhostButton><PrimaryButton type="submit" data-testid="fg-submit">{editing ? "Save" : "Add"}</PrimaryButton></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
